import { useState } from "react";

import { useBackend } from "../app/BackendProvider";
import { describeAppLoginError } from "./appGate";
import sentinelleMark from "../assets/brand/sentinelle-mark.svg";

/**
 * Écran de connexion **plein écran et bloquant** de la porte d'accès applicative
 * (login principal, contrat v1.38 §8bis — décision utilisateur 2026-09-09).
 * Tant qu'il est affiché, rien de Sentinelle n'est visible ni utilisable.
 *
 * - `Identifiant` : **nom de compte** (ex. `poste-nuit`), pas une adresse
 *   e-mail — `<input type="text">`, aucune validation d'e-mail (brief §2).
 * - `401` → « Identifiants invalides » (aucune distinction de cas) ;
 *   `422` → « Identifiant ou mot de passe manquant » (brief §3).
 * - **Aucune inscription, aucun « mot de passe oublié », aucune gestion de
 *   comptes** : les accès sont créés hors-ligne par l'administrateur
 *   (`apps/api/src/bin/user.js`).
 *
 * Succès → `onAuthenticated()` : le parent (`AppGate`) relance le démarrage
 * (`GET /auth/me`) et l'application se rend normalement — SAUF si ce
 * second appel échoue à son tour (`false` renvoyé). Signalé par l'utilisateur
 * (2026-09-20, « je boucle sur la page de connexion ») : avec de vrais bons
 * identifiants, `POST /auth/login` peut réussir tout en laissant le
 * `GET /auth/me` qui suit échouer — l'ancien code rebasculait alors sur un
 * formulaire flambant neuf, SANS AUCUNE ERREUR (l'échec vivait dans `AppGate`,
 * ce composant-ci venait d'être démonté). Vu de l'utilisateur : une boucle
 * muette, indiscernable d'un mot de passe refusé. Affiché maintenant.
 */
export function LoginScreen({ onAuthenticated }: { onAuthenticated: () => Promise<boolean> }) {
  const backend = useBackend();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await backend.api.appLogin(username.trim(), password);
      const confirmed = await onAuthenticated();
      if (!confirmed) {
        // Identifiants acceptés, mais la reconnexion qui suit échoue quand
        // même (cookie non honoré côté serveur) — pas les mêmes mots qu'un
        // 401 (« Identifiants invalides » serait faux ici et raviverait un
        // doute déjà résolu par le premier succès).
        setError("Connexion acceptée mais non confirmée par le serveur — réessayez dans un instant.");
        setBusy(false);
      }
      // `confirmed === true` : ce composant est sur le point d'être démonté
      // (l'app se rend normalement) — rien à réinitialiser ici.
    } catch (err) {
      setError(describeAppLoginError(err));
      setBusy(false);
    }
  }

  return (
    <div
      className="mae-app"
      style={{ alignItems: "center", justifyContent: "center", background: "var(--sentinelle-paper)" }}
    >
      <form
        onSubmit={submit}
        style={{
          width: "min(380px, 92vw)",
          background: "var(--sentinelle-surface)",
          border: "1px solid var(--sentinelle-rule)",
          padding: "26px 24px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 4 }}>
          {/* Marque officielle (2026-09-11) — écran clair : la marque seule (pas le
              lockup blanc, pensé pour fond sombre) + le nom en texte. */}
          <img src={sentinelleMark} alt="" style={{ height: 24, width: "auto", flex: "none" }} />
          <span
            style={{
              font: "700 14px/1 var(--bulma-family-secondary)",
              letterSpacing: "0.16em",
              color: "var(--sentinelle-ink)",
            }}
          >
            SENTINELLE
          </span>
        </div>
        <p className="ovl" style={{ marginBottom: 20 }}>
          Connexion
        </p>

        <div className="field">
          <label className="label is-small" htmlFor="app-username">
            Identifiant
          </label>
          <div className="control">
            <input
              id="app-username"
              className="input is-small"
              type="text"
              inputMode="text"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
            />
          </div>
        </div>

        <div className="field">
          <label className="label is-small" htmlFor="app-password">
            Mot de passe
          </label>
          <div className="control">
            <input
              id="app-password"
              className="input is-small"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
        </div>

        {error ? (
          <p className="help is-danger" role="alert" style={{ marginBottom: 10 }}>
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          className={`button is-small is-fullwidth ${busy ? "is-loading" : ""}`}
          style={{ background: "var(--sentinelle-ink)", color: "#fff", borderColor: "var(--sentinelle-ink)", fontWeight: 600 }}
          disabled={busy}
        >
          Se connecter
        </button>

        <p
          className="is-size-7 has-text-grey"
          style={{ marginTop: 16, lineHeight: 1.5 }}
        >
          Cet identifiant sert uniquement au contrôle d'accès à Sentinelle. Aucune donnée personnelle
          n'est collectée, aucune donnée n'est cédée à des tiers.
        </p>
      </form>
    </div>
  );
}
